import React, { useState, useEffect } from "react";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import httpRequest from "../httpRequest";
import { Box } from "@mui/material";

const TotalSpendBySupplierCard = ({ invoiceData }) => {
  const [totalSpendData, setTotalSpendData] = useState([]);
  const [error, setError] = useState(null);
  const defaultCurrency = "SAR";
  const conversionRates = {};
  const colors = ["#008FD3", "#046C7A", "#256F3A", "#BA066C", "#E03C31"];

  useEffect(() => {
    if (invoiceData) {
      calculateTotalSpend(invoiceData);
    }
  }, [invoiceData]);

  useEffect(() => {
    const fetchConversionRates = async () => {
      try {
        const response = await fetch("https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/sar.json");
        const data = await response.json();
        if (data && data.sar) {
          for (const [currency, rate] of Object.entries(data.sar)) {
            conversionRates[currency.toUpperCase()] = rate;
          }
        }
      } catch (error) {
        console.error("Error fetching conversion rates:", error);
      }
    };

    fetchConversionRates();
  }, []);

  const convertToSAR = (amount, currency) => {
    if (currency === defaultCurrency || !conversionRates[currency]) {
      return amount;
    }
    return amount / conversionRates[currency];
  };

  const calculateTotalSpend = async (invoiceData) => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-vendors`);
      if (!response.data || !response.data.vendors) {
        throw new Error("Invalid response from server");
      }

      const vendorsMap = response.data.vendors.reduce((acc, vendor) => {
        acc[vendor.vendor_code] = vendor.vendor_name;
        return acc;
      }, {});

      const spendMap = {};

      for (const invoice of invoiceData) {
        try {
          const parsedData = JSON.parse(invoice.parsed_data);
          const amount = parseFloat(parsedData["Invoice Amount"]?.replace(/,/g, ""));
          const currency = parsedData["Currency"];
          const vendorCode = invoice.vendor_code;
          const vendorName = vendorsMap[vendorCode];

          if (isNaN(amount) || !currency || !vendorCode || !vendorName || vendorCode.trim() === "") {
            throw new Error("Invalid data");
          }

          if (!spendMap[vendorCode]) {
            spendMap[vendorCode] = {
              vendorName: vendorName,
              totalAmount: convertToSAR(amount, currency)
            };
          } else {
            spendMap[vendorCode].totalAmount += convertToSAR(amount, currency);
          }
        } catch (e) {
          console.error("Skipping invoice due to error:", e);
        }
      }

      const totalSpendArray = Object.values(spendMap).map(item => ({
        ...item,
        totalAmount: parseFloat(item.totalAmount.toFixed(2))
      }));

      totalSpendArray.sort((a, b) => b.totalAmount - a.totalAmount);

      const top5Vendors = totalSpendArray.slice(0, 5);

      setTotalSpendData(top5Vendors);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to load vendor data. Please try again later.");
    }
  };

  // Calculate total spend of top 5 vendors
  const totalSpend = totalSpendData.reduce((acc, item) => acc + item.totalAmount, 0);

  // Function to format amount with abbreviation (K for thousand, M for million)
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    } else {
      return amount.toFixed(2);
    }
  };

  return (
    <Card
      header={<CardHeader avatar={<Icon name="show" />} status="*Top 5 Vendors" subtitleText="Total amount spent per supplier" titleText="Total Spend by Supplier" />}
    >
      {error ? (
        <div style={{ color: "red", textAlign: "center" }}>{error}</div>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center">
          <PieChart width={window.innerWidth > 500 ? 500 : window.innerWidth - 50} height={475}>
            <Pie
              data={totalSpendData}
              dataKey="totalAmount"
              nameKey="vendorName"
              cx={window.innerWidth > 500 ? 250 : (window.innerWidth - 50) / 2}
              cy="50%"
              innerRadius={70}
              outerRadius={150}
              label={({ name }) => `${formatAmount(totalSpendData.find(item => item.vendorName === name).totalAmount)} ${defaultCurrency}`}
              paddingAngle={1}
              minAngle={10}
            >
              {totalSpendData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name, { payload }) => [`${defaultCurrency} ${value}`, name]} />
<Legend
  align="center"
  wrapperStyle={{
    width: "auto",
    height: "auto"
  }}
/>
          </PieChart>
        </Box>
      )}
    </Card>
  );
};

export default TotalSpendBySupplierCard;
